import { ZodError } from 'zod';
import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type UpdateCurrentParsedInvoiceMutationArguments,
  type UpdateCurrentParsedInvoiceResponse,
} from './types';
import {
  zodUpdateCurrentParsedInvoiceEndpointSchema,
  zodUpdateCurrentParsedInvoicePayloadSchema,
} from './validations';

export const updateCurrentParsedInvoice = async ({
  parsedInvoiceData,
  processId,
}: UpdateCurrentParsedInvoiceMutationArguments) => {
  try {
    const response = await vestigasApi
      .put(
        ENDPOINT.INVOICE_REPROCESSING.UPDATE_CURRENT_PARSED_INVOICE(processId),
        {
          json: parsedInvoiceData,
          validate: {
            body: zodUpdateCurrentParsedInvoicePayloadSchema,
            pathSegments: zodUpdateCurrentParsedInvoiceEndpointSchema,
          },
        },
      )
      .json<UpdateCurrentParsedInvoiceResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating current parsed invoice data',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.updateCurrentParsedInvoice.error404, {
            ns: 'data',
          });
          break;
        }

        case 422: {
          errorMessage = i18n.t(($) => $.updateCurrentParsedInvoice.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.updateCurrentParsedInvoice.error.generic,
            { ns: 'data' },
          );
        }
      }
    } else if (error instanceof ZodError) {
      errorMessage = i18n.t(
        ($) => $.updateCurrentParsedInvoice.error.zodValidation,
        { ns: 'data' },
      );
    } else {
      errorMessage = i18n.t(($) => $.updateCurrentParsedInvoice.error.generic, {
        ns: 'data',
      });
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
